<template>
  <div class="iframe-container">
    <div v-if="isLoading" class="rive-loader-container">
      <canvas
        ref="riveCanvas"
        :width="canvasWidth"
        :height="canvasHeight"
      />
    </div>
    <iframe 
      v-if="isMounted && iframeSrc"
      ref="iframeRef"
      :key="src"
      :id="id"
      :src="iframeSrc" 
      :allow="allow"
      :crossorigin="crossorigin"
      :frameborder="frameborder"
      :style="{ height: iframeHeight }"
      @load="handleLoad"
      @error="handleError"
      v-bind="$attrs"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { useRiveLoader } from '@/composables/useRiveLoader';
import { useInstance } from '@/composables/useInstance';

const props = withDefaults(defineProps<{
  src: string;
  allow?: string;
  offsetPadding?: number;
  id?: string;
  crossorigin?: string;
  frameborder?: string | number;
}>(), {
  offsetPadding: 0,
});

const emit = defineEmits<{
  (e: 'load', event: Event): void;
}>();

const { $vuetify } = useInstance();
const isLoading = ref(true);
const size = computed(() => $vuetify.breakpoint.smAndDown ? 120 : 164);
const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeSrc = ref<string | null>(null);
const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
  iframeSrc.value = props.src;
});

const { riveCanvas, canvasWidth, canvasHeight } = useRiveLoader({
  width: size,
  height: size,
});

const iframeHeight = computed(() => {
  const baseHeight = 'calc(100vh - 56px)';
  if (props.offsetPadding > 0) {
    return `calc(100vh - 56px - ${props.offsetPadding}px)`;
  }
  return baseHeight;
});

const handleLoad = (event: Event) => {
  isLoading.value = false;
  emit('load', event);
};

const handleError = () => {
  isLoading.value = false;
};

watch(() => props.src, (newSrc) => {
  if (isMounted.value) {
    iframeSrc.value = newSrc;
  }
  isLoading.value = true;
});

defineExpose({
  iframeRef,
  getIframeElement: () => iframeRef.value,
  getContentWindow: () => iframeRef.value?.contentWindow,
});
</script>

<style scoped>
.iframe-container {
  line-height: 0;
  position: relative;
}

iframe {
  border: none;
  width: 100%;
  height: calc(100vh - 56px);
  scrollbar-width: none;
  display: block;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
      display: none;
  }
}

.rive-loader-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

</style> 